import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Injectable,
  Input,
  type OnInit,
  Output,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-regular-svg-icons';

export interface Sortable {
  param: string;
  ascending: boolean;
}

@Directive()
@Injectable()
export class SortableTableHeaderControl {
  sortable: Sortable;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSort: EventEmitter<Sortable> = new EventEmitter();

  sort(param: string, ascendingByDefault: boolean) {
    // if not already sorted by this param, make whole new setting
    if (!this.sortable || this.sortable.param !== param) {
      this.sortable = {
        param: param,
        ascending: ascendingByDefault ? true : false,
      };
    } else {
      // else only change direction
      this.sortable.ascending = !this.sortable.ascending;
    }

    // A new reference needs to be created because properties are being updated on the sortable object in memory.
    // But, especially for signals, there needs to be a new reference to know when there is a change.
    this.onSort.emit({ ...this.sortable });
  }
}

@Directive({
  selector: '[sortable-table-header]',
  providers: [SortableTableHeaderControl],
})
export class SortableTableHeaderDirective {
  private sortControl = inject(SortableTableHeaderControl);

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() get onSort() {
    return this.sortControl.onSort;
  }
}

@Component({
  selector: '[sortable]',
  styleUrls: ['./sortable-table-header.component.scss'],
  template: `
    <ng-content></ng-content>

    @if (isActive && isAscending) {
      <fa-icon [icon]="faCaretUp"></fa-icon>
    }
    @if (isActive && !isAscending) {
      <fa-icon [icon]="faCaretDown"></fa-icon>
    }
  `,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SortableTableHeaderComponent implements OnInit {
  private sortControl = inject(SortableTableHeaderControl);

  @HostBinding('class.active')
  get isActive() {
    return this.sortControl.sortable && this.sortControl.sortable.param === this.param;
  }

  get isAscending() {
    return this.sortControl.sortable && this.sortControl.sortable.ascending;
  }
  // whatever parameter the user wants to sort
  @Input('sortable')
  param: string;

  // sort ascending by default (on first click)
  @Input()
  ascendingByDefault = true;

  // sort by this header by default?
  @Input()
  default: boolean;

  @HostBinding('class.clickable')
  isClickable = true;

  @HostListener('click')
  clickColumn() {
    this.sortControl.sort(this.param, this.ascendingByDefault);
  }

  ngOnInit() {
    if (this.default) {
      this.sortControl.sort(this.param, this.ascendingByDefault);
    }
  }

  protected readonly faCaretUp = faCaretUp;
  protected readonly faCaretDown = faCaretDown;
}
